import React from "react";
import { Path, useFieldArray, useForm } from "react-hook-form";
import { FormTextControl, RowFormText } from "../Common/Forms/FormText";
import KaAlert from "../Common/KaAlert";
import EntityModal, { ModalMode } from "../Common/EntityModal";
import useDisplayMessage from "../Common/useDisplayMessage";
import { BranchDto } from "../../requests/useGetBranches";
import useUpsertBranch, { BranchUpsertDto } from "../../requests/useUpsertBranch";
import { Col, FormGroup } from "reactstrap";
import BoldButton from "../Common/BoldButton";
import { FormLabel } from "../Common/Forms/FormLabel";

interface CreateEditBranchProps {
  initialBranch: BranchDto | null;
  onDismiss: () => void;
  onUpsert: (branchName: string) => void;
  mode: ModalMode;
}

type BranchForm = Omit<BranchUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

export const CreateEditBranch = (props: CreateEditBranchProps) => {
  const displayMessage = useDisplayMessage();
  const { id, ...initbranch } = { ...props.initialBranch };
  const initEmails = initbranch.emailAddresses?.map(email => ({ email })) ?? [];
  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<BranchForm>({
    defaultValues: {
      ...initbranch,
      emailAddresses: initEmails.length === 0 ? [{ email: '' }] : initEmails,
    }
  });

  const emailAddresses = useFieldArray({ control, name: 'emailAddresses' });

  const validateUniqueEmail = (value: string) => {
    if (value && getValues('emailAddresses').map(f => f.email).filter(e => e === value).length > 1) {
      return 'Email already exists';
    }
    return true;
  };

  const removeEmail = (index: number) => {
    emailAddresses.remove(index);
    trigger('emailAddresses');
  }

  const upsert = useUpsertBranch(props.initialBranch?.id, {
    onSuccess: () => props.onUpsert(getValues().name),
    onError: (e) => displayMessage.fail(e.message),
  });

  const request = (formValue: BranchForm) => {
    upsert.request({
      ...formValue,
      emailAddresses: formValue.emailAddresses.map(e => e.email).filter(e => !!e),
    });
  }

  const labelSpace = 4;
  const inputSpace = 7;

  const formProps = (name: Path<BranchForm>) => ({
    labelSpace,
    inputSpace,
    name: name,
    setValue,
    register,
    errors,
  });

  const emailControl = (index: number) =>
    <FormTextControl
      {...formProps(`emailAddresses.${index}.email`)}
      displayName='Email address'
      options={{
        onChange: () => trigger('emailAddresses'),
        validate: validateUniqueEmail,
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Email Address is invalid'
        }
      }} />

  return <EntityModal
    mode={props.mode}
    entityName="branch"
    onHide={() => props.onDismiss()}
    isSaving={upsert.isLoading && !upsert.isError}
    onSubmit={handleSubmit(request)}>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText {...formProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
      <RowFormText {...formProps('number')}
        displayName="Branch Number"
        options={{
          validate: { length: v => v.length < 51 || 'Number cannot be longer than 50 characters' }
        }} />
      <RowFormText {...formProps("addressStreet")}
        displayName="Street"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...formProps("addressCity")}
        displayName="City"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address City cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...formProps("addressState")}
        displayName="State"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address State cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...formProps("addressPostalCode")}
        displayName="Postal Code"
        options={{
          validate: {
            length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters'
          }
        }} />
      <RowFormText {...formProps("addressCountry")}
        displayName="Country"
        options={{
          validate: {
            length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters'
          }
        }} />
      <RowFormText {...formProps('phone')}
        options={{
          validate: { length: v => v.length < 51 || 'Phone cannot be longer than 50 characters' }
        }} />
      {
        emailAddresses.fields.map((field, index) => {
          const isFirst = index === 0;
          const showX = emailAddresses.fields.length > 1;
          return <FormGroup row key={field.id}>
            {isFirst &&
              <Col xs={{ size: labelSpace }}>
                <FormLabel displayName='Email' {...formProps(`emailAddresses.${index}.email`)} />
              </Col>
            }
            <Col xs={{ offset: (isFirst ? 0 : labelSpace), size: inputSpace - (showX ? 1 : 0) }}>
              {emailControl(index)}
            </Col>
            {
              showX && props.mode !== ModalMode.View &&
              <Col xs="1" className="pe-0">
                <BoldButton className='btn-close btn-sm mt-2' onClick={() => removeEmail(index)} />
              </Col>
            }
          </FormGroup>
        })}
      <FormGroup row>
        <Col xs={{ offset: labelSpace, size: inputSpace }} className='text-end'>
          <BoldButton
            className='col-auto btn-ghost-primary'
            disabled={emailAddresses.fields.length >= 10}
            onClick={() => emailAddresses.append({ email: '' })}>
            Add Email
          </BoldButton>
        </Col>
      </FormGroup>
  </EntityModal>
};
